import { amountToInteger } from '@actual-app/core/shared/util';

/**
 * Lê o que o dono digitou no campo de valor e devolve CENTAVOS, positivo.
 * `null` = não dá pra usar (vazio, zero — ou vira zero depois de arredondar
 * pra centavos — ou não é número).
 *
 * ⚠️⚠️ A vírgula decimal é o ponto perigoso: `parseFloat('1.234,56')` devolve
 * `1.234` — um compromisso de R$ 1.234,56 viraria R$ 1,23, sem erro nenhum na
 * tela. Por isso a normalização é explícita aqui e não delegada ao parser da
 * plataforma.
 *
 * ⚠️ Sempre POSITIVO. Quem grava a condição do agendado é que aplica o sinal
 * de despesa — assim o formulário nunca depende de o dono lembrar de digitar
 * o menos, e digitar o menos também não inverte nada.
 *
 * ⚠️⚠️ FIX ROUND 1 (Important 2) — a checagem de zero é feita DEPOIS de
 * converter pra centavos, não antes. `'0,001'` tem `numero = 0.001` (não é
 * zero, passaria pela checagem antiga), mas `amountToInteger(0.001)` = `0`
 * (arredonda pra baixo de meio centavo) — a função devolvia `0` em vez de
 * `null`, e `FormularioCompromisso` só bloqueia quando o retorno é `null`.
 * Resultado: um compromisso de "R$ 0,00" era criado de verdade, contrariando
 * o próprio propósito da função ("compromisso de R$ 0 não é compromisso").
 *
 * ⚠️ FIX ROUND 1 (Minor) — separador de milhar REPETIDO (`'1.234.567'`,
 * `'1,234,567'`) não é decimal. A heurística "o último separador é o
 * decimal" sozinha leria `'1.234.567'` como R$ 1.234,567 (um valor 1000x
 * menor). Quando o MESMO caractere aparece mais de uma vez e o último grupo
 * tem exatamente 3 dígitos, é sempre separador de milhar — nunca decimal
 * (nenhuma moeda usa 3 casas decimais, e um decimal de verdade só aparece
 * uma vez).
 */
export function interpretarValor(texto: string): number | null {
  const limpo = texto
    .replace(/R\$/gi, '')
    .replace(/\s/g, '')
    .replace(/[−-]/g, '');

  if (limpo === '') return null;

  // Separador de milhar vs. decimal: o ÚLTIMO separador é o decimal — a não
  // ser que o MESMO caractere se repita com um grupo final de 3 dígitos
  // (ver FIX ROUND 1, Minor, acima): aí é tudo separador de milhar.
  const ultimaVirgula = limpo.lastIndexOf(',');
  const ultimoPonto = limpo.lastIndexOf('.');
  const posDecimal = Math.max(ultimaVirgula, ultimoPonto);

  let normalizado: string;
  if (posDecimal === -1) {
    normalizado = limpo;
  } else {
    const separadorDecimal = limpo[posDecimal];
    const ocorrencias = limpo.split(separadorDecimal).length - 1;
    const grupoFinal = limpo.slice(posDecimal + 1);

    if (ocorrencias > 1 && grupoFinal.length === 3) {
      normalizado = limpo.split(separadorDecimal).join('');
    } else {
      const inteiro = limpo.slice(0, posDecimal).replace(/[.,]/g, '');
      const fracao = grupoFinal.replace(/[.,]/g, '');
      normalizado = `${inteiro}.${fracao}`;
    }
  }

  if (!/^\d+(\.\d+)?$/.test(normalizado)) return null;

  const numero = Number(normalizado);
  if (!Number.isFinite(numero)) return null;

  const centavos = amountToInteger(numero);
  if (centavos === 0) return null;

  return centavos;
}
